import { AlertTriangle, CheckCircle2, Download } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Progress } from '@/components/ui/progress'
import { comoTexto } from '@/lib/errores'
import { descargarRechazos, filasConProblema } from './rechazos'

const FASES = {
  inactivo: 'Sin empezar',
  cargando: 'Escribiendo en la empresa',
  cerrando: 'Cerrando y cuadrando la contabilidad',
  hecho: 'Terminado',
  error: 'Se interrumpió',
}

/**
 * EL AVANCE DE LA CARGA, con las fases nombradas por separado.
 *
 * Una barra sola que va del 0 al 100 durante cuatro minutos hace pensar que se colgó. Aquí
 * se dice en qué está —escribiendo, cerrando— y por qué hoja va, que es lo que permite al
 * asesor decirle al cliente que tiene delante cuánto falta.
 *
 * Si se corta, NO se pierde: lo escrito ya está sellado en la carga de la empresa y cada
 * hoja recuerda por qué tramo iba. Reanudar es volver a pulsar, y solo se manda lo que
 * faltaba — repetir un tramo de saldos duplicaría la deuda del cliente.
 *
 * Deshacer ya no vive aquí: cada tipo de dato es su propia carga y se deshace desde su tarjeta.
 */
export default function ProgresoMigracion({
  progreso,
  hojas = [],
  onCargar,
  onReanudar,
  cargando,
  bloqueos = [],
  onIrAHoja,
  etiquetaDe = (e) => e,
}) {
  const p = progreso || { fase: 'inactivo' }
  const pct = p.filasTotal ? Math.round(((p.filasEscritas || 0) / p.filasTotal) * 100) : null
  const trabado = bloqueos.length > 0
  const quedan = hojas.some((h) => h.entidad && h.estado !== 'omitida' && h.estado !== 'cargada')

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="space-y-3 pt-6">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-medium text-neutral-900 dark:text-neutral-50">{FASES[p.fase] || p.fase}</span>
            {p.fase === 'hecho' && <CheckCircle2 size={16} className="text-success-600 dark:text-success-300" />}
            {p.fase === 'error' && <AlertTriangle size={16} className="text-danger-700 dark:text-danger-300" />}
            {p.entidad && (p.fase === 'cargando' || p.fase === 'cerrando') && (
              <Badge variant="secondary">{etiquetaDe(p.entidad)}</Badge>
            )}
            {p.hojaActual && p.fase === 'cargando' && (
              <Badge variant="outline">{p.hojaActual}</Badge>
            )}
          </div>

          {(p.fase === 'cargando' || p.fase === 'cerrando') && (
            <>
              <Progress value={pct ?? 0} indeterminado={p.fase === 'cerrando'} />
              <p className="text-caption tabular-nums text-neutral-500 dark:text-neutral-400">
                {(p.filasEscritas || 0).toLocaleString('es-SV')} de {(p.filasTotal || 0).toLocaleString('es-SV')} filas
                {p.hojasTotal ? ` · ${p.hojasHechas} de ${p.hojasTotal} secciones` : ''}
              </p>
            </>
          )}

          {p.fase === 'error' && (
            <>
              {/* El motivo Y dónde pasó: sin lo segundo, "se cayó la conexión" no dice si se
                  perdieron tres filas o treinta mil. */}
              {/* `comoTexto` porque el api no siempre manda una cadena aquí, y pintar el
                  objeto crudo tumbaba la pantalla entera con el trabajo a medias dentro. */}
              <p className="text-body-sm text-danger-700 dark:text-danger-300">
                {comoTexto(p.error, 'La carga se interrumpió.')}
              </p>
              <p className="text-caption text-neutral-500 dark:text-neutral-400">
                Lo que ya entró está guardado y marcado. Al reanudar solo se manda lo que faltaba.
              </p>
              <Button onClick={onReanudar} disabled={cargando}>
                Reanudar donde se quedó
              </Button>
            </>
          )}

          {/* Tras cargar UN tipo de dato la fase queda en «hecho» y aún puede quedar lo demás. */}
          {(p.fase === 'inactivo' || p.fase === 'hecho') && quedan && (
            <div className="space-y-2">
              {/*
                La puerta. Este botón ESCRIBE en la empresa de un cliente real, y estaba
                siempre habilitado: se podía cargar un proyecto a medias —hojas sin sección,
                campos requeridos sin mapear, bloqueantes del cuadre sin resolver— y
                descubrirlo después. Los avisos existían, pero eran texto en tres pantallas
                distintas que no se enteraban unas de otras.
              */}
              <Button onClick={onCargar} disabled={cargando || trabado}>
                Cargar todo lo pendiente
              </Button>
              <p className="text-caption text-neutral-500 dark:text-neutral-400">
                Entra cada tipo de dato en su propia carga, en orden. También puedes cargarlos de a uno desde las
                tarjetas de abajo.
              </p>
              {trabado && (
                <div className="space-y-1">
                  <p className="flex items-start gap-1.5 text-body-sm text-warning-700 dark:text-warning-300">
                    <AlertTriangle size={15} className="mt-0.5 shrink-0" />
                    Antes de escribir en la empresa hay que resolver esto:
                  </p>
                  <ul className="space-y-1 pl-6">
                    {bloqueos.map((b) => (
                      <li key={b.clave} className="text-body-sm text-neutral-600 dark:text-neutral-400">
                        {b.hojaId ? (
                          // Clicable: decir qué falta sin llevar hasta ahí obliga a buscarlo a mano.
                          <button
                            type="button"
                            className="text-left underline decoration-dotted underline-offset-2 hover:text-primary-700 dark:hover:text-primary-300"
                            onClick={() => onIrAHoja?.(b.hojaId)}
                          >
                            {b.motivo}
                          </button>
                        ) : (
                          b.motivo
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {hojas.length > 0 && (
        <ul className="space-y-1.5">
          {hojas.map((h) => {
            // Las filas que no entraron se pueden LLEVAR desde aquí. Antes esta pantalla solo
            // decía cuántas eran: para saber por qué había que volver al ensayo, hoja por hoja.
            const problemas = filasConProblema(h.carga?.filasMalas || h.ensayo?.filas || [])
            return (
              <li key={h.hojaId} className="flex flex-wrap items-center gap-2 text-body-sm">
                <span className="min-w-0 flex-1 truncate text-neutral-700 dark:text-neutral-300">{h.nombreHoja}</span>
                {h.carga ? (
                  <>
                    <span className="tabular-nums text-neutral-500 dark:text-neutral-400">{h.carga.escritas} escritas</span>
                    {h.carga.errores > 0 && <Badge variant="danger">{h.carga.errores} no entraron</Badge>}
                    {problemas.length > 0 && (
                      <Button variant="ghost" size="xs" onClick={() => descargarRechazos(problemas, h.nombreHoja)}>
                        <Download size={13} className="mr-1" /> Ver por qué
                      </Button>
                    )}
                  </>
                ) : (
                  <span className="text-caption text-neutral-400 dark:text-neutral-500">
                    {h.loteHecho >= 0 ? `va por el tramo ${h.loteHecho + 1}` : 'pendiente'}
                  </span>
                )}
              </li>
            )
          })}
        </ul>
      )}

    </div>
  )
}
